import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Label } from "../../../../ui/label";
import { Button } from "../../../../ui/button";
import { Textarea } from "../../../../ui/textarea";
import {
  SparklesIcon,
  InfoIcon,
  CheckIcon,
  MessageSquareTextIcon,
} from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { Workflow } from "../../../../../services/WorkflowService";
import {
  GenerationConfig,
  GENERATION_DOMAINS,
  exampleFilledUserMessageFromVariables,
  mergePlaceholdersWithGenerationVars,
  placeholdersUnusedInUserTemplate,
  getTeacherWorkflowInputSchema,
} from "./types";
import { PromptVariablesEditor, applyVariablesToConfig } from "./PromptVariablesEditor";
import {
  STRUCTURED_PROMPT_SECTION,
  STRUCTURED_PROMPT_SECTION_DESCRIPTION,
  STRUCTURED_PROMPT_SECTION_TITLE,
} from "./promptCardSection";

interface StepPromptsProps {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
  workflows: Workflow[];
}

const StepPrompts: React.FC<StepPromptsProps> = ({ config, onChange, workflows }) => {
  const isStructured = config.generation_mode === "structured";
  const useProdSeeds = config.generation_use_prod_seeds;

  const allPlaceholders = useMemo(
    () =>
      mergePlaceholdersWithGenerationVars(
        config.generation_request_system_prompt || "",
        config.generation_user_template || "",
        config.generation_variables,
        config.generation_computed_placeholders
      ),
    [
      config.generation_request_system_prompt,
      config.generation_user_template,
      config.generation_variables,
      config.generation_computed_placeholders,
    ]
  );

  const workflowInputSchemaKeys = useMemo(() => {
    if (config.generation_teacher_type !== "workflow" || !config.generation_model) {
      return undefined;
    }
    const schema = getTeacherWorkflowInputSchema(workflows, config.generation_model);
    if (!schema) {
      return undefined;
    }
    return Object.keys(schema);
  }, [config.generation_teacher_type, config.generation_model, workflows]);

  return (
    <div className="space-y-6">
      {isStructured ? (
        <StructuredPromptCard
          config={config}
          onChange={onChange}
          workflows={workflows}
          allPlaceholders={allPlaceholders}
          workflowInputSchemaKeys={workflowInputSchemaKeys}
        />
      ) : useProdSeeds ? (
        <Card className="border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-start gap-3">
              <InfoIcon className="w-5 h-5 text-maitai-green mt-0.5 shrink-0" />
              <div className="space-y-1">
                <div className="text-sm font-gc-med text-foreground">Prompts from production data</div>
                <p className="text-xs text-muted-foreground">
                  Your selected production seeds provide the user messages directly.
                  Prompt format is determined by the real traffic.
                  Configure how responses should look in the Responses step.
                </p>
              </div>
            </div>
            <DatasetRequestSystemPromptField config={config} onChange={onChange} bordered />
          </CardContent>
        </Card>
      ) : (
        <FreeformPromptCard config={config} onChange={onChange} />
      )}
    </div>
  );
};

function DatasetRequestSystemPromptField({
  config,
  onChange,
  bordered,
}: {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
  bordered?: boolean;
}) {
  return (
    <div
      className={
        bordered
          ? "pt-4 border-t border-border space-y-3"
          : "space-y-3"
      }
    >
      <div className="flex items-center gap-2">
        <MessageSquareTextIcon className="w-4 h-4 text-maitai-green" />
        <Label className="text-sm font-gc-med text-foreground">
          System prompt <span className="text-xs text-muted-foreground font-gc-reg">(optional)</span>
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        Added as the system message on each generated training example. It is not sent to the teacher
        (workflow, agent, or model); use the Responses step for teacher-only instructions when using a model teacher.
      </p>
      <Textarea
        value={config.generation_request_system_prompt}
        onChange={(e) => onChange({ generation_request_system_prompt: e.target.value })}
        placeholder="e.g. You are a support bot. Keep answers under three sentences."
        rows={3}
      />
    </div>
  );
}

function FreeformPromptCard({
  config,
  onChange,
}: {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
}) {
  const toggleDomain = (domainId: string) => {
    const current = config.generation_domains || [];
    const updated = current.includes(domainId)
      ? current.filter((d) => d !== domainId)
      : [...current, domainId];
    onChange({ generation_domains: updated });
  };

  const selectAllDomains = () => {
    const allIds = GENERATION_DOMAINS.map((d) => d.id);
    const allSelected = allIds.every((id) => config.generation_domains.includes(id));
    onChange({ generation_domains: allSelected ? [] : allIds });
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SparklesIcon className="w-5 h-5 text-maitai-green" />
            Prompt domains <span className="text-red-500">*</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={selectAllDomains}>
            {config.generation_domains.length === GENERATION_DOMAINS.length ? "Deselect All" : "Select All"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GENERATION_DOMAINS.map((domain) => {
            const selected = config.generation_domains.includes(domain.id);
            return (
              <button
                key={domain.id}
                type="button"
                onClick={() => toggleDomain(domain.id)}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-colors",
                  selected ? "border-maitai-green bg-maitai-green/5" : "border-border hover:border-maitai-green/50"
                )}
              >
                <div className={cn(
                  "mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                  selected ? "border-maitai-green bg-maitai-green" : "border-muted-foreground/40"
                )}>
                  {selected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <div className="text-sm font-gc-med text-foreground">{domain.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{domain.description}</div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {config.generation_domains.length} of {GENERATION_DOMAINS.length} domains selected
        </p>

        <div className="pt-4 border-t border-border space-y-2">
          <Label className="text-sm font-gc-med text-foreground">
            Domain context <span className="text-xs text-muted-foreground">(optional)</span>
          </Label>
          <Textarea
            value={config.generation_context}
            onChange={(e) => onChange({ generation_context: e.target.value })}
            placeholder="e.g. Customer support for a SaaS billing product, focus on subscription management and payment issues"
            rows={3}
          />
        </div>

        <DatasetRequestSystemPromptField config={config} onChange={onChange} bordered />
      </CardContent>
    </Card>
  );
}

function StructuredPromptCard({
  config,
  onChange,
  workflows,
  allPlaceholders,
  workflowInputSchemaKeys,
}: {
  config: GenerationConfig;
  onChange: (partial: Partial<GenerationConfig>) => void;
  workflows: Workflow[];
  allPlaceholders: string[];
  workflowInputSchemaKeys: string[] | undefined;
}) {
  const variables = config.generation_variables || [];

  const unusedInUserTemplate = useMemo(
    () => placeholdersUnusedInUserTemplate(allPlaceholders, config.generation_user_template || ""),
    [allPlaceholders, config.generation_user_template]
  );

  const exampleFromVariablesPreview = useMemo(
    () => exampleFilledUserMessageFromVariables(config.generation_user_template || "", variables),
    [config.generation_user_template, variables]
  );

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
          <SparklesIcon className="w-5 h-5 text-maitai-green" />
          Prompt <span className="text-red-500">*</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={STRUCTURED_PROMPT_SECTION}>
          <div className="space-y-2">
            <div className={STRUCTURED_PROMPT_SECTION_TITLE}>System prompt</div>
            <p className={STRUCTURED_PROMPT_SECTION_DESCRIPTION}>
              Stored on each training example (supports{" "}
              <code className="text-[11px] bg-background px-1 py-px rounded border border-border">{"{{placeholders}}"}</code>
              {" "}like the user message). Not sent to the teacher.
            </p>
            <Textarea
              value={config.generation_request_system_prompt}
              onChange={(e) => onChange({ generation_request_system_prompt: e.target.value })}
              placeholder="e.g. Classify the user message below according to the policy."
              rows={3}
            />
          </div>
        </div>

        <PromptVariablesEditor
          variables={variables}
          onVariablesChange={(next) =>
            applyVariablesToConfig(next, onChange, {
              generation_teacher_type: config.generation_teacher_type,
              generation_workflow_input: config.generation_workflow_input,
              workflowInputSchemaKeys,
            })
          }
          workflows={workflows}
        />

        {unusedInUserTemplate.length > 0 ? (
          <div className={STRUCTURED_PROMPT_SECTION}>
            <div className="space-y-1.5">
              <div className={STRUCTURED_PROMPT_SECTION_TITLE}>Unused in user message template</div>
              <p className={STRUCTURED_PROMPT_SECTION_DESCRIPTION}>
                These placeholders are not referenced in the user message template with{" "}
                <code className="text-[11px] bg-background px-1 py-px rounded border border-border">{"{{name}}"}</code>
                . They may still appear in the system prompt or as computed workflow args.
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {unusedInUserTemplate.map((name) => (
                <code
                  key={name}
                  className="text-[11px] font-mono bg-background border border-border px-1.5 py-0.5 rounded text-foreground"
                >
                  {`{{${name}}}`}
                </code>
              ))}
            </div>
          </div>
        ) : null}

        <div className={STRUCTURED_PROMPT_SECTION}>
          <div className="space-y-2">
            <div className={STRUCTURED_PROMPT_SECTION_TITLE}>User message template</div>
            <Textarea
              value={config.generation_user_template}
              onChange={(e) => onChange({ generation_user_template: e.target.value })}
              placeholder={"Review: {{review_text}}\n\nClassify the sentiment of this review."}
              rows={4}
            />
          </div>
          <p className={STRUCTURED_PROMPT_SECTION_DESCRIPTION}>
            Use{" "}
            <code className="text-[11px] bg-background px-1 py-px rounded border border-border">{"{{placeholder}}"}</code>{" "}
            for parts the model should vary. Names should match your Variables list when using ordered variables.
          </p>
        </div>

        {variables.length > 0 && (config.generation_user_template || "").trim() ? (
          <div className={STRUCTURED_PROMPT_SECTION}>
            <div className="space-y-1.5">
              <div className={STRUCTURED_PROMPT_SECTION_TITLE}>Example user message (from variable examples)</div>
              <p className={STRUCTURED_PROMPT_SECTION_DESCRIPTION}>
                Placeholders without an example use{" "}
                <code className="text-[11px] bg-background px-1 py-px rounded border border-border">(example name)</code>{" "}
                the same way generation does.
              </p>
            </div>
            <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-40 overflow-y-auto p-3 rounded-md bg-background border border-border">
              {exampleFromVariablesPreview}
            </pre>
          </div>
        ) : null}

        {allPlaceholders.length === 0 && (config.generation_request_system_prompt || config.generation_user_template) ? (
          <div
            className={cn(
              STRUCTURED_PROMPT_SECTION,
              "border-amber-200 bg-amber-50/80 dark:border-amber-800 dark:bg-amber-900/20"
            )}
          >
            <p className="text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2 leading-snug">
              <InfoIcon className="w-4 h-4 mt-0.5 shrink-0 opacity-90" />
              <span>
                No placeholders detected. Without{" "}
                <code className="text-[11px] bg-amber-100/90 dark:bg-amber-950/50 px-1 py-px rounded border border-amber-200/80 dark:border-amber-700 font-mono">
                  {"{{placeholders}}"}
                </code>
                , every generated example will have identical input messages.
              </span>
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default StepPrompts;
